/**
 * MCX P&L Multiplier Lookup
 * 
 * For MCX commodities, the price is quoted in a different unit than the lot size.
 * E.g., GOLDM: lot=100 grams, price per 10 grams → multiplier=10
 * For NSE/NFO, multiplier = lot_size (price per share × shares per lot).
 */
const MCX_PNL_MULTIPLIERS = {
  GOLDM: 10,
  GOLD: 100,
  GOLDGUINEA: 8,
  GOLDPETAL: 1,
  SILVER: 30,
  SILVERM: 5,
  SILVERMIC: 1,
  CRUDEOIL: 100,
  CRUDEOILM: 10,
  NATURALGAS: 1250,
  NATGASMINI: 250,
  COPPER: 2500,
  COPPERM: 250,
  ZINC: 5000,
  ZINCMINI: 1000,
  LEAD: 5000,
  LEADMINI: 1000,
  ALUMINIUM: 5000,
  ALUMINI: 1000,
  NICKEL: 1500,
  NICKELM: 100,
  MENTHAOIL: 360,
  COTTON: 25,
};

/**
 * Extract base symbol from trading symbol.
 * E.g., "GOLDM25MARFUT" → "GOLDM", "NIFTY25FEB26FUT" → "NIFTY"
 */
function extractBaseSymbol(tradingSymbol) {
  if (!tradingSymbol) return "";
  const match = tradingSymbol.toUpperCase().match(/^([A-Z]+)/);
  return match ? match[1] : tradingSymbol;
}

/**
 * Get the correct P&L multiplier for a trade.
 * 
 * @param {object} trade - Trade object with symbol, exchange, lot_size
 * @returns {number} The multiplier to use in P&L calculation
 */
export function getPnlMultiplier(trade) {
  // If trade has stored pnl_multiplier, use it
  if (trade.pnl_multiplier) return trade.pnl_multiplier;

  const exchange = (trade.exchange || "NSE").toUpperCase();
  const lotSize = trade.lot_size || 1;

  if (exchange === "MCX") {
    const base = extractBaseSymbol(trade.symbol || trade.trading_symbol || "");

    // Exact match first
    if (MCX_PNL_MULTIPLIERS[base] !== undefined) {
      return MCX_PNL_MULTIPLIERS[base];
    }

    // Prefix match (for derivatives like GOLDM25MARFUT)
    for (const [commodity, mult] of Object.entries(MCX_PNL_MULTIPLIERS)) {
      if (base.startsWith(commodity)) return mult;
    }
  }

  // Default: lot_size is the multiplier (correct for NSE/NFO)
  return lotSize;
}

/**
 * Calculate P&L for a trade.
 * 
 * @param {object} params
 * @param {string} params.direction - "LONG" or "SHORT"
 * @param {number} params.entryPrice
 * @param {number} params.exitPrice
 * @param {number} params.numLots
 * @param {object} params.trade - Trade object (for multiplier lookup)
 * @returns {{ pnlPoints: number, pnlAmount: number }}
 */
export function calculatePnl({ direction, entryPrice, exitPrice, numLots, trade }) {
  const multiplier = getPnlMultiplier(trade);
  const pnlPoints = direction === "LONG"
    ? exitPrice - entryPrice
    : entryPrice - exitPrice;
  const pnlAmount = pnlPoints * numLots * multiplier;
  return {
    pnlPoints: Math.round(pnlPoints * 100) / 100,
    pnlAmount: Math.round(pnlAmount * 100) / 100,
  };
}
